import { trackingIncomingMessage } from './tracking';
import { EChatActionTypes } from '../../actions/chat';
import { EOpenedChatsTypes } from '../../actions/openedChats';
import { ESendMessageActionTypes, ISendMessagePayload } from '../../actions/sendMessage';
import { ERequestApiStatus, TAppState } from '../../types/appState';
import { TOutcomingStatus } from '../../types/messages';
import { TReduxActions } from '../../types/redux/actions';
import { addNewChat } from '../../utils/addNewChat';
import { insertMessageInChatMessages, removeMessageFromChatMessages } from '../../utils/messageUtils';

function updateState(
  state: TAppState,
  { message, messageId, messagesMap }: ISendMessagePayload,
  status?: TOutcomingStatus,
) {
  const chatId = message.chatId;
  const chatMessages = removeMessageFromChatMessages(messageId, messagesMap, state.openedChats[chatId].chatMessages);

  return {
    ...state,
    openedChats: {
      ...state.openedChats,
      [chatId]: {
        ...state.openedChats[chatId],
        chatMessages: insertMessageInChatMessages(messagesMap, message, chatMessages, state.user.userId, status),
      },
    },
  };
}

export function openedChatsReducer(state: TAppState, action: TReduxActions): TAppState {
  const userId = state.user.userId;

  switch (action.type) {
    case ESendMessageActionTypes.SetPending:
      return updateState(state, action.payload, 'pending');

    case ESendMessageActionTypes.SetFailed:
      return updateState(state, action.payload, 'failed');

    case ESendMessageActionTypes.SetSuccess:
      return updateState(state, action.payload);

    case ESendMessageActionTypes.SetFirstMessageSuccess: {
      const { message, messagesMap } = action.payload;
      const firstMessageChatId = message.chatId;

      const currentChat = state.chatList.chats.find(chat => chat.chatId === firstMessageChatId);

      // Создаем чат при успешном ответе
      const openedChats = addNewChat({
        openedChats: state.openedChats,
        offer: currentChat?.data?.offer,
        currentChatId: firstMessageChatId,
      });

      // Вставляем первое сообщение после ответа по вебсокету
      const newChatMessages = insertMessageInChatMessages(messagesMap, message, [], userId);

      return {
        ...state,
        openedChats: {
          ...openedChats,
          [firstMessageChatId]: {
            ...openedChats[firstMessageChatId],
            chatMessages: newChatMessages,
            status: ERequestApiStatus.Succeed,
          },
        },
      };
    }

    case ESendMessageActionTypes.SetDelete: {
      const { chatId, messageId, messagesMap } = action.payload;

      const chatMessages = removeMessageFromChatMessages(
        messageId,
        messagesMap,
        state.openedChats[chatId].chatMessages,
      );

      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [chatId]: {
            ...state.openedChats[chatId],
            chatMessages,
          },
        },
      };
    }
    case EChatActionTypes.IncomingMessage: {
      const { message, messagesMap } = action.payload;
      const openedChat = state.openedChats[message.chatId];

      if (!openedChat) {
        return state;
      }

      if (message.userId !== userId) {
        trackingIncomingMessage(message.chatId);
      }

      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [message.chatId]: {
            ...openedChat,
            chatMessages: insertMessageInChatMessages(messagesMap, message, openedChat.chatMessages, userId),
          },
        },
      };
    }
    case EChatActionTypes.SetEditableMessage:
      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [action.payload.chatId]: {
            ...state.openedChats[action.payload.chatId],
            editableMessage: action.payload.text,
            defaultMessagePayload: action.payload.payload,
          },
        },
      };

    case EChatActionTypes.SetCaptchaToken:
      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [action.payload.chatId]: {
            ...state.openedChats[action.payload.chatId],
            captchaToken: action.payload.captchaToken,
          },
        },
      };

    case EChatActionTypes.DisableSendDefaultMessage:
      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [action.payload.chatId]: {
            ...state.openedChats[action.payload.chatId],
            sendDefaultMessage: false,
          },
        },
      };

    case EChatActionTypes.SetBubbleInputFocus:
      return {
        ...state,
        chatWindow: {
          ...state.chatWindow,
          isBubbleInputFocused: action.payload.isBubbleInputFocused,
        },
      };

    case EOpenedChatsTypes.Clear:
      return {
        ...state,
        openedChats: {},
      };

    case EOpenedChatsTypes.Add:
      return {
        ...state,
        openedChats: addNewChat({
          openedChats: state.openedChats,
          offer: action.payload.offer,
          currentChatId: action.payload.chatId,
          newChat: action.payload.newChat,
        }),
      };

    case EOpenedChatsTypes.Delete:
      const openedChats = { ...state.openedChats };
      delete openedChats[action.payload];

      return {
        ...state,
        openedChats,
      };
    default:
      return state;
  }
}
